<template>
    <div class="container mx-auto">
        <!-- logo -->
        <div class="flex flex-col items-center justify-center">
            <img class="w-32" src="../assets/logo-megaluzz.png" alt="Megaluzz Logo">
        </div>

        <div class="flex justify-center mb-6" v-if="loading">
            <Loading text="Carregando dados..." :show="loading" />
        </div>

        <!-- Header -->
        <div class="flex flex-col gap-y-2 items-center justify-center mt-4">
            <h1 class="text-green-600 font-bold text-base uppercase">{{ dispatch.company?.name }}</h1>
            <h1 class="text-green-700 font-semibold text-sm uppercase">Avaliação interna de {{ dispatch.target?.name }}</h1>
        </div>

        <!-- Campos -->
        <div
            :class="{ 'bg-green-100 border border-green-700': field.lock }"
            v-for="field in form.form_body?.structure"
            :key="field.uuid"
            class="flex bg-white p-4 rounded-md mt-4 border border-gray-300"
        >
            <div class="flex flex-col flex-1">
                <h1 v-if="field.lock" class="text-red-400 mb-2 font-bold text-sm">Pergunta interna da Megaluzz</h1>

                <!-- Label -->
                <h1 v-if="field.type !== 'static_text'" class="text-green-600 mb-2 font-bold text-base">
                    {{ field.label }}
                    <small v-if="field.required" class="text-red-500">*</small>
                </h1>
                <p v-else class="text-gray-700 text-base whitespace-pre-line">{{ field.label }}</p>

                <!-- Texto -->
                <template v-if="field.type === 'text'">
                    <BaseInput v-if="field.lock" v-model="field.answer" />
                    <p v-else class="text-gray-700 text-sm">{{ field.answer || '---' }}</p>
                </template>

                <!-- Long text -->
                <template v-if="field.type === 'long_text'">
                    <BaseTextarea v-if="field.lock" rows="5" v-model="field.answer" />
                    <p v-else class="text-gray-700 text-sm whitespace-pre-line">{{ field.answer || '---' }}</p>
                </template>

                <!-- Select -->
                <template v-if="field.type === 'select'">
                    <BaseSelect
                        v-if="field.lock"
                        :options="parseSelectOptions(field.options)"
                        v-model="field.answer"
                        class="w-6/12"
                    />
                    <p v-else class="text-gray-700 text-sm">{{ field.answer || '---' }}</p>
                </template>

                <!-- Múltipla escolha (radio) -->
                <!-- Radio Button -->
				<div class="flex flex-col gap-y-4 items-center" v-if="field.type === 'multiple_choice' || field.type === 'scale'">
					<div class="flex flex-wrap gap-x-6 gap-y-4 mt-4">
						<div v-for="option in field.options" :key="option.uuid" class="flex items-center gap-x-1">
							<BaseRadio
								:value="option.label"
								:label="option.label"
								:name="field.uuid"
								v-model="field.answer"
								:disabled="true"
							/>
						</div>
					</div>
				</div>

                <!-- Checkbox -->
                <template v-if="field.type === 'checkbox'">
                    <div v-if="field.lock" class="flex flex-col gap-y-2 mt-4">
                        <div
                            v-for="option in field.options"
                            :key="option.uuid"
                            class="flex gap-x-2"
                        >
                            <BaseCheckbox :label="option.label" v-model="option.answer" />
                        </div>
                    </div>
                    <ul v-else class="text-gray-700 text-sm list-disc ml-4">
                            <li
                                v-for="option in getCheckedOptions(field)"
                                :key="option.uuid"
                            >
                            {{ option.label }}
                        </li>
                        <li v-if="!field.options.some(opt => opt.answer)">---</li>
                    </ul>
                </template>
            </div>
        </div>

        <!-- Botão de envio -->
        <div
            v-if="form.form_body?.structure.some(f => f.lock)"
            class="flex justify-center mt-6"
        >
            <button
                :disabled="loadingSubmit"
                @click="submitForm"
                class="bg-green-600 text-white px-6 py-4 rounded-md disabled:bg-gray-400 hover:bg-green-700 w-full"
            >
                <div class="flex gap-x-2 justify-center">
                    <svg
                        v-if="loadingSubmit"
                        class="animate-spin mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    Enviar
                </div>
            </button>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert';
import { DispatchService } from '../services/Entity';

export default {
    name: 'SurveyInternalView',
    setup() {
        const loading = ref(false);
        const route = useRoute();
        const router = useRouter();
        const dispatch = ref({});
        const form = ref({});
        const loadingSubmit = ref(false);

        onMounted(async () => {
            const dispatchUUID = route.params.dispatch_uuid;

            if (!dispatchUUID) {
                Swal({
                    title: 'Link inválido',
                    text: 'Solicite um novo link à Megaluzz',
                    icon: 'info'
                });
                return;
            }

            loading.value = true;
            try {
                const { data } = await DispatchService.getByUUID(dispatchUUID);
                dispatch.value = data;
                form.value = { form_body: data.form_structure };

                // Marcar checkboxes
                form.value.form_body.structure.forEach(field => {
                    if (field.type === 'checkbox') {
                        field.options.forEach(opt => {
                            opt.answer = !!opt.answer;
                        });
                    }
                });
            } catch (err) {
                Swal({
                    title: 'Erro',
                    text: 'Não foi possível carregar os dados. Atualize a página.',
                    icon: 'error'
                });
                console.error(err);
            } finally {
                loading.value = false;
            }
        });

        async function submitForm() {
            try {
                loadingSubmit.value = true;

                const uuid = dispatch.value.uuid;
                const form_structure = form.value.form_body;

                await DispatchService.update(uuid, { form_structure });

                router.push({ name: 'thanks' });
            } catch (err) {
                console.error('Erro ao enviar formulário:', err);
                Swal({
                    title: 'Erro ao enviar',
                    text: 'Ocorreu um erro ao tentar enviar o formulário. Tente novamente mais tarde.',
                    icon: 'error'
                });
                loadingSubmit.value = false;
            }
        }

        function getCheckedOptions(field) {
            return field.options.filter(opt => opt.answer);
        }

        function parseSelectOptions(options = []) {
            return options.map(opt => ({
                title: opt.label,
                value: opt.label
            }));
        }

        return {
            getCheckedOptions,
            loading,
            loadingSubmit,
            dispatch,
            form,
            submitForm,
            parseSelectOptions
        };
    }
};
</script>
